import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Button,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Paper,
  Box,
  ThemeProvider,
  CssBaseline,
} from "@mui/material";
import { createTheme } from "@mui/material/styles";

import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import Table_theme from "../Themes/TableTheme";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const detailFields = [
  "asset_det_id",
  "asset_id",
  "description",
  "purchase_at",
  "manufacturer",
  "model_no",
  "model_name",
  "sprice",
  "purchase_date",
  "warranty_number",
  "warranty_validity",
  "warranty_status",
  "service_date",
  "service_by",
  "installation_flag",
  "installation_cost",
  "qty",
  "currency",
  "status",
  "asset_condition",
  "replaced_on",
  "replaced_by",
  "replace_reason",
  "remarks",
];
const fieldLabels = {
  asset_det_id: "Asset Detail ID",
  asset_id: "Asset ID",
  description: "Description",
  purchase_at: "Purchase At",
  manufacturer: "Manufacturer",
  model_no: "Model No",
  model_name: "Model Name",
  sprice: "Selling Price",
  purchase_date: "Purchase Date",
  warranty_number: "Warranty Number",
  warranty_validity: "Warranty Valid Till",
  warranty_status: "Warranty Status",
  service_date: "Service Date",
  service_by: "Serviced By",
  installation_flag: "Installation Done",
  installation_cost: "Installation Cost",
  qty: "Quantity",
  currency: "Currency",
  status: "Status",
  asset_condition: "Condition",
  replaced_on: "Replaced On",
  replaced_by: "Replaced By",
  replace_reason: "Replace Reason",
  remarks: "Remarks",
};
const nonEditableFields = ["asset_id", "status"];

// === Theme for Font Family ===
const assetDetailTheme = createTheme({
  typography: {
    fontFamily: "Public Sans, sans-serif",
    fontSize: 13,
  },
});

const AssetDetailSubpage = ({ open, onClose, asset_id }) => {
  const [details, setDetails] = useState([]);
  const [newDetail, setNewDetail] = useState({ asset_id, status: "Active" });
  const [editDetail, setEditDetail] = useState({});
  const [activeForm, setActiveForm] = useState(null);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [showSnackbar, setShowSnackbar] = useState(false);

  // === Fetching Data ===
  const fetchDetails = useCallback(async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}/assetdetails/paginated`,
        {
          params: { asset_id, page: 1, limit: 10 },
        }
      );
      setDetails(response.data.data || []);
    } catch (err) {
      console.error("Fetch error:", err);
    }
  }, [asset_id]);

  useEffect(() => {
    if (open && asset_id) fetchDetails();
  }, [open, asset_id, fetchDetails]);

  // === Field Renderer ===
  const renderField = (field, state, setState) => {
    const isDate = ["purchase_date", "warranty_end"].includes(field);
    const sharedProps = {
      fullWidth: true,
      size: "small",
      InputLabelProps: {
        shrink: true,
        sx: { fontSize: "0.85rem", fontWeight: 500, mt: 0.5 },
      },
      inputProps: {
        style: { fontSize: "0.85rem", fontFamily: "Public Sans, sans-serif" },
      },
    };

    return isDate ? (
      <TextField
        type="date"
        label={fieldLabels[field]}
        value={state[field] || ""}
        onChange={(e) =>
          setState((prev) => ({
            ...prev,
            [field]: e.target.value,
          }))
        }
        InputLabelProps={{ shrink: true }}
        {...sharedProps}
      />
    ) : (
      <TextField
        label={fieldLabels[field] || field}
        value={state[field] || ""}
        onChange={(e) =>
          !nonEditableFields.includes(field) &&
          setState((prev) => ({ ...prev, [field]: e.target.value }))
        }
        InputProps={{
          readOnly: nonEditableFields.includes(field),
          sx: { fontSize: "0.85rem", fontFamily: "Public Sans, sans-serif" },
        }}
        {...sharedProps}
      />
    );
  };

  // === Handlers ===
  const handleAdd = async () => {
    try {
      await axios.post(`${BASE_URL}/assetdetails`, {
        ...newDetail,
        delete_flag: "N",
        data_source: "UI",
        created_by: "system",
        created_on_date: new Date().toISOString().split("T")[0],
      });
      setSnackbarMsg("Detail added");
      setShowSnackbar(true);
      fetchDetails();
      setActiveForm(null);
      setNewDetail({ asset_id, status: "Active" });
    } catch (err) {
      console.error("Add error:", err);
      setSnackbarMsg("Failed to add");
      setShowSnackbar(true);
    }
  };

  const handleUpdate = async () => {
    try {
      await axios.put(
        `${BASE_URL}/assetdetails/${editDetail.asset_detail_id}`,
        editDetail
      );
      setSnackbarMsg("Detail updated");
      setShowSnackbar(true);
      fetchDetails();
      setActiveForm(null);
    } catch (err) {
      console.error("Update error:", err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`Delete asset detail ${id}?`)) return;
    try {
      await axios.delete(`${BASE_URL}/assetdetails/${id}`);
      setSnackbarMsg("Detail deleted");
      setShowSnackbar(true);
      fetchDetails();
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  return (
    <ThemeProvider theme={assetDetailTheme}>
      <CssBaseline />
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
        <DialogTitle
          sx={{
            borderRadius: "12px",
            background: "linear-gradient(45deg, #e6f0fa, #ffffff)",
            p: 2,
            fontSize: "14px",
            fontWeight: 550,
            fontFamily: "Public Sans, sans-serif",
          }}
        >
          Asset Details for Asset ID: {asset_id}
          <IconButton
            onClick={onClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ fontFamily: "Public Sans, sans-serif" }}>
          {!activeForm && (
            <Box mb={2} display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#2db0e4",
                  color: "#fff",
                  "&:hover": { backgroundColor: "#115293" },
                  textTransform: "none",
                  fontSize: "0.85rem",
                  fontFamily: "Public Sans, sans-serif",
                }}
                onClick={() => {
                  setNewDetail({ asset_id, status: "Active" });
                  setActiveForm("add");
                }}
              >
                Add Detail
              </Button>
            </Box>
          )}

          {["add", "edit"].includes(activeForm) && (
            <Grid container spacing={1} mt={1}>
              {detailFields.map((field) => (
                <Grid item xs={12} sm={6} key={field}>
                  {renderField(
                    field,
                    activeForm === "add" ? newDetail : editDetail,
                    activeForm === "add" ? setNewDetail : setEditDetail
                  )}
                </Grid>
              ))}
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  onClick={activeForm === "add" ? handleAdd : handleUpdate}
                  sx={{
                    backgroundColor: "#2db0e4",
                    color: "#fff",
                    "&:hover": { backgroundColor: "#115293" },
                    textTransform: "none",
                    fontSize: "0.85rem",
                    mr: 1,
                    mt: 1,
                  }}
                >
                  {activeForm === "add" ? "Add" : "Update"}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setActiveForm(null)}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.85rem",
                    mt: 1,
                  }}
                >
                  Cancel
                </Button>
              </Grid>
            </Grid>
          )}

          <ThemeProvider theme={Table_theme}>
            <TableContainer component={Paper} sx={{ mt: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                      Actions
                    </TableCell>
                    {detailFields.map((f) => (
                      <TableCell
                        key={f}
                        sx={{ fontSize: "0.85rem", fontWeight: 600 }}
                      >
                        {fieldLabels[f]}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {details.length > 0 ? (
                    details.map((det) => (
                      <TableRow key={det.asset_detail_id}>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setEditDetail(det);
                              setActiveForm("edit");
                            }}
                          >
                            <EditIcon sx={{ fontSize: "16px" }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(det.asset_detail_id)}
                          >
                            <DeleteIcon sx={{ fontSize: "16px" }} />
                          </IconButton>
                        </TableCell>
                        {detailFields.map((f) => (
                          <TableCell key={f} sx={{ fontSize: "0.85rem" }}>
                            {det[f]}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={detailFields.length + 1}
                        align="center"
                      >
                        No asset details found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </ThemeProvider>
        </DialogContent>

        <DialogActions sx={{ p: "8px 24px", justifyContent: "flex-end" }}>
          <Button
            onClick={onClose}
            variant="outlined"
            color="inherit"
            sx={{
              fontSize: "0.85rem",
              color: "#555",
              borderColor: "#ccc",
              textTransform: "none",
            }}
          >
            Close
          </Button>
        </DialogActions>

        <Snackbar
          open={showSnackbar}
          autoHideDuration={3000}
          onClose={() => setShowSnackbar(false)}
          message={snackbarMsg}
        />
      </Dialog>
    </ThemeProvider>
  );
};

export default AssetDetailSubpage;
